import { useEffect, useState } from "react";
import type { Part, Values, Project } from "@proof/core";
import { useAuthoringServices } from "./services";
import { Button, Control } from "./components";
export function HostFormData({
  kind,
  onValues,
  onPart,
  project,
}: {
  kind: "profiles" | "parts";
  project?: Project;
  onValues?: (values: Values) => void;
  onPart?: (part: Part) => void;
}) {
  const services = useAuthoringServices();
  const [query, setQuery] = useState(""),
    [rows, setRows] = useState<
      { id: string; name: string; sku?: string; values?: Values }[]
    >([]),
    [selected, setSelected] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const load =
    kind === "profiles"
      ? services.listFormProfiles
      : query.trim()
        ? services.searchPartSkus || services.listPartOverrides
        : services.listPartOverrides || services.searchPartSkus;
  useEffect(() => {
    selection?.abort();
    const controller = new AbortController();
    setRows([]);
    setSelected("");
    setError("");
    if (load) {
      setBusy(true);
      void (
        kind === "profiles"
          ? services.listFormProfiles!(query, controller.signal, project)
          : load(query, controller.signal)
      )
        .then((rows) => {
          if (!controller.signal.aborted) setRows(rows);
        })
        .catch((error) => {
          if (!controller.signal.aborted) setError(error.message);
        })
        .finally(() => {
          if (!controller.signal.aborted) setBusy(false);
        });
    }
    return () => controller.abort();
  }, [load, query]);
  const [selection, setSelection] = useState<AbortController>();
  useEffect(() => () => selection?.abort(), [selection]);
  if (!load) return null;
  return (
    <section className="panel padded">
      <Control
        label={kind === "profiles" ? "Search profiles" : "Search part SKUs"}
      >
        <input value={query} onChange={(e) => setQuery(e.target.value)} />
      </Control>
      <Control
        label={
          kind === "profiles"
            ? "Existing global profile data"
            : "Existing part overrides"
        }
      >
        <select
          value={selected}
          disabled={busy}
          onChange={(e) => {
            selection?.abort();
            setSelected(e.target.value);
            const row = rows.find((r) => r.id === e.target.value);
            if (!row) return;
            if (kind === "profiles" && row.values) onValues?.(row.values);
            else if (services.loadPartOverride) {
              const c = new AbortController();
              setSelection(c);
              setBusy(true);
              void services
                .loadPartOverride(row.id, c.signal)
                .then((part) => {
                  if (!c.signal.aborted) onPart?.(part);
                })
                .catch((error) => {
                  if (!c.signal.aborted) setError(error.message);
                })
                .finally(() => {
                  if (!c.signal.aborted) setBusy(false);
                });
            }
          }}
        >
          <option value="">Choose…</option>
          {rows.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
              {r.sku ? ` (${r.sku})` : ""}
            </option>
          ))}
        </select>
      </Control>
      {error && <p role="alert">{error}</p>}
      {busy && <p role="status">Loading…</p>}
    </section>
  );
}
